import { CameraOutlined, ReloadOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "../ui";
import React from "react";
import { ThemedModal } from "./ThemedModal";
import "./ImageCaptureField.css";
import { AsyncActionButton } from "./AsyncActionButton";

const ACCENT_BORDER_WIDTH = 2;
const CAPTURE_FIRST_QUALITY = 0.88;
/** JPEG qualities tried in order while the capture exceeds the byte limit. */
const CAPTURE_FALLBACK_QUALITIES = [0.65, 0.45];
/** DOMException names of getUserMedia (PermissionDeniedError and DevicesNotFoundError are legacy names). */
const CAMERA_PERMISSION_ERRORS: readonly string[] = ["NotAllowedError", "PermissionDeniedError"];
const CAMERA_NOT_FOUND_ERRORS: readonly string[] = ["NotFoundError", "DevicesNotFoundError"];

export type ImageCaptureFieldLabels = {
  empty: React.ReactNode;
  upload: string;
  takePhoto: string;
  retake: string;
  usePhoto: string;
  cancel: string;
  previewAlt: string;
  cameraSecureContextRequired: string;
  cameraUnsupported: string;
  cameraPermissionDenied: string;
  cameraNotFound: string;
  cameraUnavailable: string;
  cameraNotReady: string;
};

export type ImageCaptureFieldProps = {
  imageUrl?: string | null;
  labels: ImageCaptureFieldLabels;
  uploading?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  capabilities?: Partial<{ upload: boolean; camera: boolean; drop: boolean; preview: boolean }>;
  /** Fachliche Bildmaße und optionaler Statusrahmen, unabhängig vom Theme. */
  dimensions?: { width: number; height: number };
  accentColor?: string;
  captureLimit?: { maxBytes: number; tooLargeMessage: React.ReactNode };
  showActions?: boolean;
  showEmptyText?: boolean;
  accept?: string;
  captureFileName?: string;
  fallbackIcon?: React.ReactNode;
  onImageSelected: (file: File) => Promise<void | boolean> | void | boolean;
  onError: (message: React.ReactNode) => void;
};

const captureMaxDimension = 1800;

export function ImageCaptureField({
  imageUrl,
  labels,
  uploading,
  disabled,
  readOnly = false,
  capabilities,
  dimensions,
  accentColor,
  captureLimit,
  showActions = true,
  showEmptyText = true,
  accept = "image/*",
  captureFileName = "photo.jpg",
  fallbackIcon,
  onImageSelected,
  onError,
}: ImageCaptureFieldProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const editable = !disabled && !uploading && !readOnly;
  const canUpload = editable && capabilities?.upload !== false;
  const canDrop = canUpload && capabilities?.drop !== false;
  const showUpload = !readOnly && capabilities?.upload !== false;
  const showCamera = !readOnly && capabilities?.camera !== false;
  const canPreview = capabilities?.preview !== false;
  const frameStyle = {
    ...(dimensions ? {
      "--mq-image-capture-width": `${dimensions.width}px`,
      "--mq-image-capture-height": `${dimensions.height}px`,
      "--mq-image-capture-ratio": `${dimensions.width} / ${dimensions.height}`,
    } : {}),
    ...(accentColor ? { borderColor: accentColor, borderWidth: ACCENT_BORDER_WIDTH } : {}),
  } as React.CSSProperties;

  const pickFile = React.useCallback(() => {
    if (!canUpload) return;
    fileInputRef.current?.click();
  }, [canUpload]);

  const handleFileInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && canUpload) void onImageSelected(file);
      event.target.value = "";
    },
    [canUpload, onImageSelected]
  );

  const openCamera = React.useCallback(() => {
    if (!editable || !showCamera) return;
    if (!window.isSecureContext) {
      onError(labels.cameraSecureContextRequired);
      pickFile();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      onError(labels.cameraUnsupported);
      pickFile();
      return;
    }
    setCameraOpen(true);
  }, [editable, showCamera, labels.cameraSecureContextRequired, labels.cameraUnsupported, onError, pickFile]);

  const uploadButton = (
    <AsyncActionButton icon={<UploadOutlined />} loading={uploading} disabled={disabled} onClick={pickFile}>
      {labels.upload}
    </AsyncActionButton>
  );

  const cameraButton = (
    <Button icon={<CameraOutlined />} disabled={disabled || uploading} onClick={openCamera}>
      {labels.takePhoto}
    </Button>
  );

  return (
    <>
      <div className="mq-image-capture-field">
        <div
          className={`mq-image-capture-field__frame${dragOver && canDrop ? " mq-image-capture-field__frame--drag-over" : ""}`}
          style={frameStyle}
          onDragEnter={(event) => {
            if (!event.dataTransfer.types.includes("Files")) return;
            event.preventDefault();
            if (canDrop) setDragOver(true);
          }}
          onDragOver={(event) => {
            if (!event.dataTransfer.types.includes("Files")) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = canDrop ? "copy" : "none";
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragOver(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setDragOver(false);
            if (!canDrop) return;
            const file = event.dataTransfer.files[0];
            if (file?.type.startsWith("image/")) void onImageSelected(file);
          }}
        >
          {imageUrl && canPreview ? (
            <button type="button" className="mq-image-capture-field__preview-trigger" onClick={() => setPreviewOpen(true)}>
              <img className="mq-image-capture-field__image" src={imageUrl} alt={labels.previewAlt} draggable={false} />
            </button>
          ) : imageUrl ? (
            <img className="mq-image-capture-field__image" src={imageUrl} alt={labels.previewAlt} draggable={false} />
          ) : (
            <div className="mq-image-capture-field__placeholder" aria-hidden="true">
              {fallbackIcon}
              {!fallbackIcon && showEmptyText ? labels.empty : null}
            </div>
          )}
          {showCamera || showUpload ? <div className="mq-image-capture-field__overlay">
            {showCamera ? <Tooltip title={labels.takePhoto}>
              <Button
                type="text"
                className="mq-image-capture-field__control"
                icon={<CameraOutlined />}
                aria-label={labels.takePhoto}
                disabled={disabled || uploading}
                onClick={openCamera}
              />
            </Tooltip> : null}
            {showUpload ? <Tooltip title={labels.upload}>
              <AsyncActionButton
                type="text"
                className="mq-image-capture-field__control"
                icon={<UploadOutlined />}
                aria-label={labels.upload}
                loading={uploading}
                disabled={disabled}
                onClick={pickFile}
              />
            </Tooltip> : null}
          </div> : null}
        </div>
        {showActions && (showCamera || showUpload) ? (
          <div className="mq-image-capture-field__actions">
            {showCamera ? cameraButton : null}
            {showUpload ? uploadButton : null}
          </div>
        ) : null}
        {showEmptyText && !imageUrl && fallbackIcon ? <span className="mq-image-capture-field__empty">{labels.empty}</span> : null}
      </div>
      <input ref={fileInputRef} type="file" accept={accept} hidden onChange={handleFileInputChange} />
      {imageUrl ? (
        <ThemedModal
          open={previewOpen}
          title={labels.previewAlt}
          footer={null}
          width="min(90vw, 1200px)"
          autoFocusFirstField={false}
          destroyOnHidden
          onCancel={() => setPreviewOpen(false)}
        >
          <div className="mq-image-capture-field__full-preview">
            <img src={imageUrl} alt={labels.previewAlt} />
          </div>
        </ThemedModal>
      ) : null}
      <ThemedModal
        open={cameraOpen}
        title={labels.takePhoto}
        footer={null}
        width="min(680px, calc(100vw - var(--mq-space-lg, 24px)))"
        destroyOnHidden
        onCancel={() => setCameraOpen(false)}
      >
        <CameraCaptureContent
          labels={labels}
          captureFileName={captureFileName}
          captureLimit={captureLimit}
          onError={onError}
          onCancel={() => setCameraOpen(false)}
          onImageSelected={(file) => editable && showCamera ? onImageSelected(file) : false}
        />
      </ThemedModal>
    </>
  );
}

type CameraCaptureContentProps = {
  labels: ImageCaptureFieldLabels;
  captureFileName: string;
  captureLimit?: ImageCaptureFieldProps["captureLimit"];
  onError: (message: React.ReactNode) => void;
  onCancel: () => void;
  onImageSelected: ImageCaptureFieldProps["onImageSelected"];
};

function CameraCaptureContent({ labels, captureFileName, captureLimit, onError, onCancel, onImageSelected }: CameraCaptureContentProps) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const previewUrlRef = React.useRef<string | null>(null);
  const [phase, setPhase] = React.useState<"camera" | "preview">("camera");
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [blob, setBlob] = React.useState<Blob | null>(null);
  const [busy, setBusy] = React.useState(false);

  const revokePreview = React.useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const stopStream = React.useCallback(() => {
    const stream = streamRef.current;
    if (stream) stream.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  React.useEffect(() => {
    let alive = true;
    if (phase !== "camera") {
      stopStream();
      return undefined;
    }

    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" } },
        });
        if (!alive) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
      } catch (err) {
        onError(cameraErrorText(err, labels));
        onCancel();
      }
    })();

    return () => {
      alive = false;
      stopStream();
    };
  }, [labels, onCancel, onError, phase, stopStream]);

  React.useEffect(() => {
    return () => {
      stopStream();
      revokePreview();
    };
  }, [revokePreview, stopStream]);

  const capturePhoto = React.useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      onError(labels.cameraNotReady);
      return;
    }

    const scale = Math.min(1, captureMaxDimension / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext("2d");
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    setBusy(true);
    void (async () => {
      try {
        const toJpeg = (quality: number) => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
        let nextBlob = await toJpeg(CAPTURE_FIRST_QUALITY);
        if (captureLimit) {
          for (const quality of CAPTURE_FALLBACK_QUALITIES) {
            if (!nextBlob || nextBlob.size <= captureLimit.maxBytes) break;
            nextBlob = await toJpeg(quality);
          }
        }
        if (!nextBlob) {
          onError(labels.cameraUnavailable);
          return;
        }
        if (captureLimit && nextBlob.size > captureLimit.maxBytes) {
          onError(captureLimit.tooLargeMessage);
          return;
        }
        stopStream();
        revokePreview();
        const nextUrl = URL.createObjectURL(nextBlob);
        previewUrlRef.current = nextUrl;
        setBlob(nextBlob);
        setPreviewUrl(nextUrl);
        setPhase("preview");
      } catch {
        onError(labels.cameraUnavailable);
      } finally {
        setBusy(false);
      }
    })();
  }, [captureLimit, labels.cameraNotReady, labels.cameraUnavailable, onError, revokePreview, stopStream]);

  const retake = React.useCallback(() => {
    revokePreview();
    setBlob(null);
    setPreviewUrl(null);
    setPhase("camera");
  }, [revokePreview]);

  const handleUsePhoto = React.useCallback(async () => {
    if (!blob) return;
    setBusy(true);
    try {
      const saved = await onImageSelected(new File([blob], captureFileName, { type: "image/jpeg" }));
      if (saved === false) return;
      revokePreview();
      onCancel();
    } finally {
      setBusy(false);
    }
  }, [blob, captureFileName, onCancel, onImageSelected, revokePreview]);

  return (
    <div>
      <div className="mq-image-capture-field__camera-preview">
        {phase === "camera" ? (
          <video ref={videoRef} autoPlay muted playsInline />
        ) : previewUrl ? (
          <img src={previewUrl} alt={labels.previewAlt} />
        ) : null}
        <canvas ref={canvasRef} hidden />
      </div>
      <div className="mq-image-capture-field__modal-actions">
        {phase === "camera" ? (
          <AsyncActionButton type="primary" icon={<CameraOutlined />} loading={busy} onClick={capturePhoto}>
            {labels.takePhoto}
          </AsyncActionButton>
        ) : (
          <>
            <AsyncActionButton type="primary" icon={<SaveOutlined />} loading={busy} disabled={!blob} onClick={() => handleUsePhoto()}>
              {labels.usePhoto}
            </AsyncActionButton>
            <Button icon={<ReloadOutlined />} disabled={busy} onClick={retake}>
              {labels.retake}
            </Button>
          </>
        )}
        <Button disabled={busy} onClick={onCancel}>
          {labels.cancel}
        </Button>
      </div>
    </div>
  );
}

function cameraErrorText(err: unknown, labels: ImageCaptureFieldLabels): React.ReactNode {
  const name = String((err as { name?: unknown } | null)?.name ?? "");
  if (CAMERA_PERMISSION_ERRORS.includes(name)) return labels.cameraPermissionDenied;
  if (CAMERA_NOT_FOUND_ERRORS.includes(name)) return labels.cameraNotFound;
  return labels.cameraUnavailable;
}
